import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, doc, onSnapshot, getDocs, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../services/firebase';
import MessageBubble from '../components/MessageBubble';
import Topbar from '../components/Topbar';
import Icon from '../components/Icon';
import Loading from '../components/Loading';
import { localeOf } from '../i18n/format';
import './ConversationView.css';

const initials = (u) => {
  if (u?.profileName) return u.profileName.trim().slice(0, 2).toUpperCase();
  return (u?.phone || '').slice(-2);
};
// Presentation only — the number is stored raw in the database.
const formatPhone = (raw) => {
  const d = (raw || '').replace(/\D/g, '');
  if (d.length === 13 && d.startsWith('55')) return `+55 (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 12 && d.startsWith('55')) return `+55 (${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`;
  return raw ? `+${d}` : '';
};
const displayName = (u) => u?.profileName || formatPhone(u?.phone);
const shortTime = (ts) => {
  if (!ts?.toDate) return '';
  const d = ts.toDate();
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString(localeOf(), { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString(localeOf(), { day: '2-digit', month: '2-digit' });
};
const sameDay = (a, b) => a && b && new Date(a).toDateString() === new Date(b).toDateString();

export default function ConversationView({ accountId, onBack, onLogout }) {
  const { t } = useTranslation();
  const dayLabel = (at) => {
    const d = new Date(at);
    const today = new Date();
    const yest = new Date();
    yest.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return t('conversations.today');
    if (d.toDateString() === yest.toDateString()) return t('conversations.yesterday');
    return d.toLocaleDateString(localeOf(), { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [botActive, setBotActive] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [chatOpen, setChatOpen] = useState(false); // mobile: list vs chat
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const autoResize = (el) => {
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  };

  useEffect(() => {
    if (!newMessage && textareaRef.current) textareaRef.current.style.height = 'auto';
  }, [newMessage]);

  useEffect(() => {
    async function fetchChannels() {
      const snap = await getDocs(collection(db, 'accounts', accountId, 'channels'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setChannels(list);
      if (list.length > 0) setSelectedChannel(list[0].id);
    }
    fetchChannels();
  }, [accountId]);

  useEffect(() => {
    if (!selectedChannel) { setUsers([]); setSelectedUser(null); setLoadingUsers(false); return; }
    setLoadingUsers(true);
    const col = collection(db, 'accounts', accountId, 'conversations', selectedChannel, 'users');
    const unsub = onSnapshot(col, (snap) => {
      const list = snap.docs.map(d => ({ phone: d.id, ...d.data() }));
      list.sort((a, b) => (b.lastMessageAt?.toMillis?.() || 0) - (a.lastMessageAt?.toMillis?.() || 0));
      setUsers(list);
      setSelectedUser(prev => prev || (list[0]?.phone ?? null));
      setLoadingUsers(false);
    });
    return () => unsub();
  }, [accountId, selectedChannel]);

  useEffect(() => {
    if (!selectedChannel || !selectedUser) { setMessages([]); return; }
    setLoadingMsgs(true);
    const ref = doc(db, 'accounts', accountId, 'conversations', selectedChannel, 'users', selectedUser);
    const unsubscribe = onSnapshot(ref, (docSnap) => {
      const data = docSnap.data();
      setMessages(data?.history || []);
      setBotActive(data?.botActive !== false);
      setLoadingMsgs(false);
    });
    return () => unsubscribe();
  }, [accountId, selectedChannel, selectedUser]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const selectUser = async (phone) => {
    setSelectedUser(phone);
    setChatOpen(true);
    const u = users.find(x => x.phone === phone);
    if (u?.unreadCount) {
      await updateDoc(doc(db, 'accounts', accountId, 'conversations', selectedChannel, 'users', phone), { unreadCount: 0 });
    }
  };

  const toggleBotStatus = async () => {
    const ref = doc(db, 'accounts', accountId, 'conversations', selectedChannel, 'users', selectedUser);
    await updateDoc(ref, { botActive: !botActive });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChannel || !selectedUser || sending) return;
    setSending(true);
    try {
      const sendManualMessage = httpsCallable(functions, 'sendManualMessage');
      await sendManualMessage({ accountId, channelId: selectedChannel, phone: selectedUser, message: newMessage });
      setNewMessage('');
    } catch (err) {
      console.error(err);
      alert(t('conversations.error'));
    } finally {
      setSending(false);
    }
  };

  const selectedObj = users.find(u => u.phone === selectedUser);

  return (
    <div className="page conv-page">
      <Topbar onBack={onBack} onLogout={onLogout} crumbs={[t('conversations.crumb')]} />

      <div className={`conversation-body ${chatOpen ? 'chat-open' : ''}`}>
        <aside className="user-list">
          <div className="list-label">{t('conversations.channelLabel')}</div>
          <select
            className="field"
            style={{ marginBottom: '12px' }}
            value={selectedChannel || ''}
            onChange={e => setSelectedChannel(e.target.value)}
          >
            {channels.length === 0 && <option value="">{t('conversations.noChannel')}</option>}
            {channels.map(c => <option key={c.id} value={c.id}>{c.name || c.phoneNumber || c.id}</option>)}
          </select>

          <div className="list-label">{t('conversations.conversationsLabel')}</div>
          {loadingUsers && <Loading label={t('conversations.loading')} />}
          {!loadingUsers && users.map(u => (
            <button
              key={u.phone}
              className={`user-button ${selectedUser === u.phone ? 'active' : ''}`}
              onClick={() => selectUser(u.phone)}
            >
              <span className="user-avatar">{initials(u)}</span>
              <span className="user-main">
                <span className="user-top">
                  <span className="user-name">{displayName(u)}</span>
                  {u.lastMessageAt && <span className="user-time">{shortTime(u.lastMessageAt)}</span>}
                </span>
                <span className="user-bottom">
                  <span className="user-preview">{u.lastMessage || t('common.dash')}</span>
                  {u.unreadCount > 0 && selectedUser !== u.phone && (
                    <span className="unread-badge">{u.unreadCount > 99 ? '99+' : u.unreadCount}</span>
                  )}
                </span>
              </span>
            </button>
          ))}
          {!loadingUsers && selectedChannel && users.length === 0 && <div className="user-empty">{t('conversations.emptyList')}</div>}
        </aside>

        <div className="conversation-panel">
          {selectedUser ? (
            <>
              <div className="bot-status">
                <div className="conv-contact">
                  <button className="mobile-back" onClick={() => setChatOpen(false)} aria-label={t('common.back')}>
                    <Icon name="arrow-left" size={20} />
                  </button>
                  <span className="user-avatar">{initials(selectedObj)}</span>
                  <div>
                    <div className="conv-contact-name">{displayName(selectedObj)}</div>
                    {selectedObj?.profileName && <div className="conv-contact-phone">{formatPhone(selectedUser)}</div>}
                  </div>
                </div>
                <div className="conv-status-actions">
                  <span className={`status-pill ${botActive ? 'active' : 'inactive'}`}>
                    <span className="status-dot" />
                    {botActive ? t('conversations.botActive') : t('conversations.manualService')}
                  </span>
                  <button className={`btn btn-sm ${botActive ? 'btn-ghost' : 'btn-primary'}`} onClick={toggleBotStatus}>
                    <Icon name={botActive ? 'user' : 'rotate'} size={16} />
                    {botActive ? t('conversations.takeOver') : t('conversations.reactivateBot')}
                  </button>
                </div>
              </div>

              <div className="message-list">
                {loadingMsgs && messages.length === 0 && <Loading label={t('conversations.loadingMessages')} />}
                {messages.map((msg, idx) => {
                  const prev = messages[idx - 1];
                  const showSep = msg.at && (!prev || !sameDay(prev.at, msg.at));
                  return (
                    <React.Fragment key={idx}>
                      {showSep && <div className="date-sep"><span>{dayLabel(msg.at)}</span></div>}
                      <MessageBubble {...msg} />
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {!botActive && (
                <div className="manual-reply">
                  <textarea
                    ref={textareaRef}
                    rows={1}
                    placeholder={t('conversations.messagePlaceholder')}
                    value={newMessage}
                    onChange={(e) => { setNewMessage(e.target.value); autoResize(e.target); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                  />
                  <button className="send-manual-message" onClick={handleSendMessage} disabled={sending} aria-label={t('conversations.send')}>
                    {sending
                      ? <span className="spinner send-spinner" />
                      : <Icon name="send" size={18} />}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="user-empty" style={{ margin: 'auto' }}>{t('conversations.selectConversation')}</div>
          )}
        </div>
      </div>
    </div>
  );
}
